/**
 * NGS pre-checkout reward offer.
 *
 * Intercepts Checkout when the shopper has not yet unlocked the free mystery
 * item, and offers them a way to close the gap — or to carry on unchanged.
 *
 * Patterned on sad_boy_saga's checkout intercept, including the parts that look
 * like details and are not:
 *
 *   CAPTURE PHASE. The listener is bound with `true`. Checkout is a submit
 *   button inside a form; by the time a bubbling handler runs, the theme's own
 *   handlers may already have started the navigation. Capture is the only place
 *   that reliably wins.
 *
 *   STAND DOWN RATHER THAN GUESS. If the cart is not confirmed, or the shopper
 *   already qualifies, or nothing on offer can actually close the gap, the click
 *   passes straight through. An interceptor that fires with nothing useful
 *   behind it is worse than no interceptor.
 *
 *   NO ONCE-PER-CART GUARD. sad_boy_saga removed theirs deliberately: a shopper
 *   who closes the modal to think about it could otherwise never get back to the
 *   offer. Re-showing is not a trap because the escape link is always present
 *   and always works on the first click.
 */

const CFG = window.SpkMystery || {};
const REWARD_VARIANT = Number(CFG.variantId) || 0;
const THRESHOLD = Number(CFG.thresholdCents) || 0;
const ENABLED = CFG.enabled === true;

const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
const withJs = (u, f) => { const b = u || f; return b.endsWith('.js') ? b : `${b}.js`; };
const CART_URL = withJs(routes.cart_url, '/cart');
const ADD_URL = routes.cart_add_url || '/cart/add.js';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

let root = null;
let lastFocus = null;
/** Set once the shopper chooses to continue, so the next click sails through. */
let standDown = false;

/* -------------------------------------------------------------------------- */

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Qualifying subtotal — reward lines excluded, post-discount.
 * Mirrors spk-cart-core.qualifyingSubtotal. Duplicated deliberately rather than
 * imported: this file must work whether or not the gate module loaded.
 */
function qualifying(cart) {
  return (cart.items || [])
    .filter((l) => l.id !== REWARD_VARIANT)
    .reduce((s, l) => s + l.final_line_price, 0);
}

async function getCart() {
  const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart read failed: ${res.status}`);
  return res.json();
}

/* -------------------------------------------------------------------------- */
/* modal                                                                      */
/* -------------------------------------------------------------------------- */

function cardsThatClose(gap) {
  return $$('[data-spk-offer-card]', root).filter(
    (c) => Number(c.dataset.priceCents) >= gap
  );
}

function paint(gap) {
  const gapEl = $('[data-spk-offer-gap]', root);
  if (gapEl) gapEl.textContent = money(gap);

  // Only show what actually closes the gap. Offering a $12 pair of socks against
  // a $65 shortfall is an offer the shopper cannot take, and reads as a bug.
  let shown = 0;
  for (const card of $$('[data-spk-offer-card]', root)) {
    const closes = Number(card.dataset.priceCents) >= gap;
    card.hidden = !closes;
    if (closes) shown += 1;
  }
  const empty = $('[data-spk-offer-empty]', root);
  if (empty) empty.hidden = shown > 0;
  return shown;
}

function open() {
  lastFocus = document.activeElement;
  root.hidden = false;
  document.documentElement.style.overflow = 'clip';
  $('[data-spk-offer-continue]', root)?.focus();
  document.addEventListener('keydown', onKey);
}

function close() {
  root.hidden = true;
  document.documentElement.style.overflow = '';
  document.removeEventListener('keydown', onKey);
  lastFocus?.focus?.();
}

function onKey(e) {
  if (e.key === 'Escape') { e.stopPropagation(); close(); }
}

/* -------------------------------------------------------------------------- */
/* interception                                                               */
/* -------------------------------------------------------------------------- */

function isCheckoutTrigger(el) {
  if (!el) return false;
  return !!el.closest(
    'button[name="checkout"], input[name="checkout"], [data-spk-checkout], a[href$="/checkout"], a[href*="/checkout?"]'
  );
}

document.addEventListener(
  'click',
  async (e) => {
    if (!ENABLED || !REWARD_VARIANT || THRESHOLD <= 0) return;
    if (!root) return;
    if (standDown) return;
    if (!isCheckoutTrigger(e.target)) return;
    // An accelerated wallet button (Shop Pay / G Pay) goes straight to that
    // provider's sheet. Interrupting it is both hostile and unreliable.
    if (e.target.closest('[data-shopify="dynamic-checkout-cart"], shopify-accelerated-checkout')) return;

    let cart;
    try {
      cart = await getCart();
    } catch {
      return; // unconfirmed -> never interrupt
    }

    const sub = qualifying(cart);
    if (sub <= 0) return;
    if (sub >= THRESHOLD) return;               // already unlocked
    const gap = THRESHOLD - sub;
    if (!cardsThatClose(gap).length) return;    // nothing to offer

    e.preventDefault();
    e.stopImmediatePropagation();
    paint(gap);
    open();
  },
  true
);

/* -------------------------------------------------------------------------- */
/* wiring                                                                     */
/* -------------------------------------------------------------------------- */

function bind() {
  root = $('[data-spk-offer]');
  if (!root) return;

  root.addEventListener('click', async (e) => {
    if (e.target.closest('[data-spk-offer-dismiss]')) { close(); return; }

    if (e.target.closest('[data-spk-offer-continue]')) {
      // Let the very next checkout click through, then re-arm. Without the
      // re-arm a shopper who continues, changes their mind and adds more would
      // never see the offer again in that session.
      standDown = true;
      close();
      const btn = $('button[name="checkout"], input[name="checkout"]');
      if (btn) btn.click();
      setTimeout(() => { standDown = false; }, 5000);
      return;
    }

    const add = e.target.closest('[data-spk-offer-add]');
    if (!add) return;
    const id = Number(add.dataset.variantId);
    if (!id) return;

    add.disabled = true;
    const label = add.textContent;
    add.textContent = 'ADDING…';
    try {
      await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      // Full reload rather than a partial repaint: the add must also re-run the
      // reward gate, the drawer and the totals, and this is a once-per-session
      // moment where correctness beats saving a paint.
      window.location.reload();
    } catch {
      add.disabled = false;
      add.textContent = label;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bind, { once: true });
} else {
  bind();
}
